import React, { useState } from "react";
import Modal from "@material-ui/core/Modal";
import { InvestForm } from "../Forms/InvestForm";
import CurrencyDisplay from "../Currency/CurrencyDisplay";

export const Invest = ({ fromWallet, isHome, plan, img, planDescription }) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleCardClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleOpen();
  };

  // Determine theme based on plan name (Default to Blue, use Purple for Prestige)
  const title = planDescription?.title || plan?.planName || "";
  const isPrestige = title.toLowerCase().includes("prestige");

  // Theme configuration dictionaries
  const theme = {
    headerGradient: isPrestige
      ? "bg-gradient-to-br from-[#1a0b35] via-[#2d125c] to-[#430b8a]"
      : "bg-gradient-to-br from-[#041133] via-[#082266] to-[#0d36a3]",
    buttonBg: isPrestige
      ? "bg-[#5b1ce6] hover:bg-[#4d17c4]"
      : "bg-[#1a4bdf] hover:bg-[#153eb8]",
    textColor: isPrestige ? "text-[#5b1ce6]" : "text-[#1a4bdf]",
    pillBg: isPrestige ? "bg-[#38166d]" : "bg-[#16368c]",
    iconColor: isPrestige ? "#5b1ce6" : "#1a4bdf",
  };

  return (
    <>
      {/* --- Investment Modal --- */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className="h-screen w-screen flex justify-center outline-0 transition duration-500"
      >
        <InvestForm
          open={open}
          setOpen={setOpen}
          handleOpen={handleOpen}
          handleClose={handleClose}
        />
      </Modal>

      {fromWallet ? (
        /* --- Wallet Quick Action Button --- */
        <div onClick={handleCardClick} className="w-full">
          <div className=" py-4 px-2 flex flex-col items-center text-center  cursor-pointer hover:bg-blue-50/50 transition-colors active:scale-95">
            <div className="w-12 h-12 rounded-full bg-[#f0f4ff] flex items-center justify-center text-[#1a4bdf] mb-3 relative">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
              </svg>
            </div>
            <h4 className="text-[13px] font-bold text-slate-800 mb-1">
              Invest
            </h4>
            <p className="text-[10px] text-slate-500 font-medium leading-tight">
              Grow your wealth
            </p>
          </div>
        </div>
      ) : (
        /* --- High-Fidelity Investment Plan Card --- */
        <div
          onClick={handleCardClick}
          className="w-full bg-white rounded-[24px] shadow-[0_10px_40px_rgba(0,0,0,0.06)] border border-gray-100 cursor-pointer group hover:shadow-[0_15px_50px_rgba(0,0,0,0.1)] hover:-translate-y-1 transition-all duration-300 overflow-hidden font-sans flex flex-col"
        >
          {/* --- Header (Gradient & 3D Assets) --- */}
          <div
            className={`relative h-[180px] w-full ${theme.headerGradient} overflow-hidden px-6 py-6 flex justify-between bg-center`}
            style={
              img
                ? {
                    backgroundImage: `url('${img}')`,
                    backgroundSize: "cover",
                  }
                : {}
            }
          >
            {/* Background Texture/Rays placeholder */}
            <div
              className="absolute inset-0 opacity-30 mix-blend-overlay pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 80% 20%, white, transparent 60%)",
              }}
            ></div>

            {/* Right: Return Info */}
            <div className="relative z-10 flex flex-col items-end">
              {/* Duration Pill */}
              <div
                className={`${theme.pillBg} text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full mb-3 shadow-sm border border-white/10`}
              >
                {plan?.duration || 7} DAYS • FIXED CYCLE
              </div>

              {/* Yield Display */}
              <div className="flex flex-col items-end text-white">
                <span className="text-[12px] font-bold text-white/80 uppercase tracking-widest mb-[-4px]">
                  Up To
                </span>
                <span className="text-[46px] font-black leading-none tracking-tighter drop-shadow-md">
                  {(plan?.profitProduction * 7).toFixed(0)}%
                </span>
                <span className="text-[12px] font-bold uppercase tracking-widest text-white/80 mt-1">
                  Return
                </span>
              </div>
            </div>
          </div>

          {/* --- Body Section --- */}
          <div className="px-2 pt-6 pb-6 flex flex-col flex-1">
            {/* Title & Description */}
            <div className="mb-6">
              <h2 className="text-[22px] font-bold text-[#071330] leading-tight mb-2">
                {title}
              </h2>
              <p className="text-[14px] text-slate-500 font-medium leading-relaxed">
                {planDescription?.description ||
                  "Designed for individuals starting their investment journey or those seeking steady and consistent progress."}
              </p>
            </div>

            {/* Features List */}
            {planDescription?.features && (
              <div className="flex flex-col gap-3 mb-6">
                {planDescription.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    {/* Custom Checkmark matching theme */}
                    <div className="mt-0.5 shrink-0">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={theme.iconColor}
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    </div>
                    <span className="text-[14px] text-slate-600 font-medium leading-snug">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex-1"></div>

            {/* Divider */}
            <div className="h-px w-full bg-gray-200 mb-6"></div>

            {/* Entry Range Section */}
            <div className="flex flex-col mb-6">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                Entry Range
              </span>

              <div
                className={`text-[24px] font-black ${theme.textColor} tracking-tight mb-1`}
              >
                <CurrencyDisplay
                  usdAmount={plan?.minAmount}
                  showCurrency={true}
                />
              </div>

              <p className="text-[12px] text-slate-400 font-medium leading-snug mb-1">
                For payment information, please reach out to our support team.
              </p>

              <div className="text-[18px] font-bold text-[#071330] tracking-tight">
                <CurrencyDisplay
                  usdAmount={plan?.maxAmount}
                  showCurrency={true}
                />
              </div>
            </div>

            {/* Action Button */}
            <button
              className={`w-full ${theme.buttonBg} text-white font-bold text-[16px] py-4 rounded-[14px] flex items-center justify-center gap-2 transition-colors active:scale-95 shadow-md`}
            >
              Invest Now
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};
